import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutChangeEvent, StyleSheet, TouchableOpacity, View, ViewStyle, useWindowDimensions } from 'react-native';
import Animated, {
    AnimatedStyle,
    runOnJS,
    useAnimatedProps,
    useAnimatedRef,
    useAnimatedStyle,
    useScrollViewOffset,
    useSharedValue,
    withTiming,
} from 'react-native-reanimated';

import { useNavigation } from '@react-navigation/native';

import { ArcadeProductIcon } from '@/assets/icons/ArcadeProductIcon';
import { CasinoProductIcon } from '@/assets/icons/CasinoProductIcon';
import { PickemProductIcon } from '@/assets/icons/PickemProductIcon';
import { PredictionProductIcon } from '@/assets/icons/PredictionProductIcon';
import { RipsProductIcon } from '@/assets/icons/RipsProductIcon';
import { SettingsGearIcon } from '@/assets/icons/SettingsGearIcon';
import { SweepsProductIcon } from '@/assets/icons/SweepsProductIcon';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { SelectableProduct, useSelectableProducts } from '@/hooks/use-available-products';
import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useProductSwitchModal } from '@/hooks/use-product-switch';
import { common } from '@/styles/styles';
import { isWeb, showScrollIndicator } from '@/utils/constants-platform-specific';
import { triggerHapticFeedback } from '@/utils/haptic-feedback';

import { Position, useProductSwitcherChipsPositions } from './ProductSwitcherChipsPositionsProvider';

type ScaleAnimationStyle = AnimatedStyle<Pick<ViewStyle, 'transform'>>;

export const PRODUCT_CHIPS_HEIGHT = 56;
const TRANSITION_DURATION_TIMING = 300;
const FADE_IN_DURATION_TIMING = 200;

/**
 * Calculates the scroll offset needed to center a target item within a container,
 * while ensuring the item remains fully visible.
 */
const calculateCenteredScrollOffset = (
    targetX: number,
    targetWidth: number,
    containerWidth: number,
    contentWidth: number
): number => {
    // Ideal offset to center the item
    const centerOffset = targetX - containerWidth / 2 + targetWidth / 2;

    // Minimum offset to show the item's right edge
    const minOffsetToShowItem = targetX + targetWidth - containerWidth;

    // Maximum offset to show the item's left edge
    const maxOffsetToShowItem = targetX;

    // Start with center, but ensure item is fully visible
    let offset = centerOffset;
    offset = Math.max(offset, minOffsetToShowItem); // don't cut off right edge
    offset = Math.min(offset, maxOffsetToShowItem); // don't cut off left edge

    // Clamp to valid scroll bounds
    const maxScrollOffset = Math.max(0, contentWidth - containerWidth);
    return Math.max(0, Math.min(offset, maxScrollOffset));
};

/**
 * Hook to manage the indicator animation for the product switcher chips.
 * Handles the translateX and width animations for the selection indicator.
 */
const useIndicatorAnimation = (initialPosition?: Position) => {
    const translateX = useSharedValue(initialPosition?.x ?? 0);
    const width = useSharedValue(initialPosition?.width ?? 0);

    const style = useAnimatedStyle(() => ({
        transform: [{ translateX: translateX.value }],
        width: width.value,
    }));

    const animateTo = useCallback(
        (position: Position, duration = TRANSITION_DURATION_TIMING, onComplete?: () => void) => {
            translateX.value = withTiming(position.x, { duration });
            width.value = withTiming(
                position.width,
                { duration },
                onComplete
                    ? () => {
                          'worklet';
                          runOnJS(onComplete)();
                      }
                    : undefined
            );
        },
        [translateX, width]
    );

    const snapTo = useCallback(
        (position: Position) => {
            translateX.value = position.x;
            width.value = position.width;
        },
        [translateX, width]
    );

    return { style, animateTo, snapTo, translateX, width };
};

/**
 * Product switcher chips component that displays the available products as chips.
 * This is the component that is used in the lobby of each product
 */
export const ProductSwitcherChips = memo(({ scaleAnimation }: { scaleAnimation?: ScaleAnimationStyle }) => {
    const activeFromStore = useJurisdictionStore(state => state.product);

    const { changeProduct, switchingAvailable } = useProductSwitchModal();

    const { setPosition, getPosition, scrollViewOffset } = useProductSwitcherChipsPositions();
    const storedPosition = getPosition(activeFromStore);
    const { style: indicatorStyle, animateTo, snapTo, width } = useIndicatorAnimation(storedPosition);
    // If we already have the position stored (e.g. returning from product transition),
    // start fully visible so there's no animate-from-zero flash.
    const opacity = useSharedValue(isWeb ? 1 : storedPosition ? 1 : 0);

    const animatedRef = useAnimatedRef<Animated.ScrollView>();
    // Sync scroll position updates from this ScrollView to the shared scrollViewOffset.
    // This keeps the normal component's scroll state synchronized with the transition component.
    useScrollViewOffset(animatedRef, scrollViewOffset);

    // Opacity style for fade-in effect, combined with indicatorBaseStyle in the component
    const opacityStyle = useAnimatedStyle(() => ({
        opacity: opacity.value,
    }));

    const onLayout = useCallback(
        (product: Product, e: LayoutChangeEvent) => {
            const position = { x: e.nativeEvent.layout.x, width: e.nativeEvent.layout.width };
            setPosition(product, position);

            if (product === activeFromStore) {
                if (width.value === 0) {
                    snapTo(position);
                    opacity.value = withTiming(1, { duration: FADE_IN_DURATION_TIMING });
                } else {
                    animateTo(position);
                }
            }
        },
        [activeFromStore, animateTo, opacity, setPosition, snapTo, width]
    );

    const onPress = (product: Product) => {
        triggerHapticFeedback();

        if (!switchingAvailable || product === activeFromStore) {
            return;
        }

        changeProduct(product);
    };

    const productChips = useSelectableProducts();

    const refs = useRef<{ [key in Product]: View | null }>({} as { [key in Product]: View });

    const { width: screenWidth } = useWindowDimensions();

    // Update indicator position based on active product changes
    useEffect(() => {
        // On web, recalculate positions when screen width changes
        if (isWeb && refs.current) {
            Object.entries(refs.current).forEach(([product, ref]) => {
                ref?.measure((x, y, productWidth) => {
                    const position = { x, width: productWidth };
                    setPosition(product as Product, position);
                    if (product === activeFromStore) {
                        snapTo(position);
                        opacity.value = 1;
                    }
                });
            });
        } else {
            // For other platforms, animate to active product position when the product is
            // changed directly from the jurisdiction store (e.g.: deep links)
            const activePosition = getPosition(activeFromStore);
            if (activePosition) {
                animateTo(activePosition);
                opacity.value = withTiming(1, { duration: FADE_IN_DURATION_TIMING });
            }
        }
    }, [activeFromStore, animateTo, getPosition, opacity, screenWidth, setPosition, snapTo]);

    const animatedProps = useAnimatedProps(() => ({
        // Force the ScrollView to match the shared scroll offset.
        // This ensures the component initializes with the correct scroll position
        // when transitioning back from the ProductSwitcherChipsTransition component.
        contentOffset: {
            x: scrollViewOffset.value,
            y: 0,
        },
    }));

    if (!switchingAvailable) {
        return null;
    }

    return (
        <Animated.ScrollView
            horizontal
            ref={animatedRef}
            animatedProps={animatedProps}
            contentContainerStyle={styles.contentContainer}
            showsHorizontalScrollIndicator={showScrollIndicator}
            alwaysBounceHorizontal={false}
        >
            <Animated.View style={[common.row, styles.contentWrapper, scaleAnimation]}>
                <Animated.View pointerEvents="none" style={[styles.indicator, indicatorStyle, opacityStyle]} />
                {productChips.map(product => (
                    <View
                        key={product}
                        onLayout={e => onLayout(product, e)}
                        ref={ref => {
                            refs.current[product] = ref;
                        }}
                    >
                        <TouchableOpacity onPress={() => onPress(product)}>
                            <ChipText product={product} isSelected={activeFromStore === product} />
                        </TouchableOpacity>
                    </View>
                ))}
                <DefaultProductGearButton />
            </Animated.View>
        </Animated.ScrollView>
    );
});

const DefaultProductGearButton = () => {
    const { t } = useTranslation('product_switch_menu');
    const navigation = useNavigation();

    const onPress = () => {
        triggerHapticFeedback();
        navigation.navigate('DefaultProductSelect');
    };

    return (
        <TouchableOpacity
            onPress={onPress}
            accessibilityRole="button"
            accessibilityLabel={t('change_default_product')}
            style={styles.gearButton}
        >
            <SettingsGearIcon />
        </TouchableOpacity>
    );
};

/**
 * Handles the animated transition of the chips when switching products.
 *
 * Synchronization strategy:
 * 1. This component initializes with the same scrollViewOffset as the normal ProductSwitcherChips
 * 2. All scrolling and indicator animations are handled entirely within this component
 * 3. The scrollViewOffset shared value is updated as the user/animation scrolls
 * 4. When the transition completes and ProductSwitcherChips is rendered again, it automatically
 *    initializes at the correct scroll position because it uses the same scrollViewOffset
 *
 * This component should be used only in the ProductTransitionOverlay.
 */
export const ProductSwitcherChipsTransition = memo(
    ({
        fromProduct,
        toProduct,
        skipAnimation = false,
        onIndicatorAtRest,
    }: {
        fromProduct: Product;
        toProduct: Product;
        skipAnimation?: boolean;
        onIndicatorAtRest?: () => void;
    }) => {
        const [containerWidth, setContainerWidth] = useState(0);
        const [contentWidth, setContentWidth] = useState(0);

        const onLayout = useCallback((e: LayoutChangeEvent) => {
            setContainerWidth(e.nativeEvent.layout.width);
        }, []);

        const onContentSizeChange = useCallback((w: number) => {
            setContentWidth(w);
        }, []);

        const { getPosition, scrollViewOffset } = useProductSwitcherChipsPositions();
        const animatedRef = useAnimatedRef<Animated.ScrollView>();

        // Sync scroll position updates from this ScrollView back to the shared scrollViewOffset.
        // This ensures the normal ProductSwitcherChips component will be in sync when it's rendered again.
        useScrollViewOffset(animatedRef, scrollViewOffset);

        // When skipAnimation=true (shouldFadeOut phase), initialize at toProduct so the indicator
        // is already at its final position on first render — no snap-back visible.
        const startPosition = skipAnimation ? getPosition(toProduct) : getPosition(fromProduct);
        const { style: indicatorStyle, animateTo, snapTo } = useIndicatorAnimation(startPosition);

        const hasAnimatedBefore = useRef(false);
        const hasScrolledBefore = useRef(false);

        // Indicator animation: runs immediately once; does not require layout dimensions.
        useEffect(() => {
            if (hasAnimatedBefore.current) return;
            hasAnimatedBefore.current = true;

            const finalPosition = getPosition(toProduct);

            if (skipAnimation) {
                if (finalPosition) snapTo(finalPosition);
                onIndicatorAtRest?.();
                return;
            }

            const initialPosition = getPosition(fromProduct);
            if (initialPosition) {
                snapTo(initialPosition);
            }
            if (finalPosition) {
                animateTo(finalPosition, undefined, onIndicatorAtRest);
            } else {
                onIndicatorAtRest?.();
            }
        }, [animateTo, fromProduct, getPosition, onIndicatorAtRest, skipAnimation, snapTo, toProduct]);

        // Wait for layout (containerWidth/contentWidth) before scrolling; unlike the indicator effect above, this can't fire on the first render.
        useEffect(() => {
            if (!containerWidth || !contentWidth) return;
            if (hasScrolledBefore.current) return;
            hasScrolledBefore.current = true;

            const finalPosition = getPosition(toProduct);
            if (!finalPosition) return;

            const offset = calculateCenteredScrollOffset(
                finalPosition.x,
                finalPosition.width,
                containerWidth,
                contentWidth
            );
            animatedRef.current?.scrollTo({ x: offset, y: 0, animated: !skipAnimation });
        }, [animatedRef, containerWidth, contentWidth, getPosition, skipAnimation, toProduct]);

        const productChips = useSelectableProducts();

        const animatedProps = useAnimatedProps(() => ({
            // Initialize the ScrollView with the same scroll offset as the normal component.
            // This ensures a seamless visual transition when switching from the normal component.
            contentOffset: {
                x: scrollViewOffset.value,
                y: 0,
            },
        }));

        return (
            <Box height={PRODUCT_CHIPS_HEIGHT}>
                <Animated.ScrollView
                    horizontal
                    ref={animatedRef}
                    animatedProps={animatedProps}
                    contentContainerStyle={styles.contentWrapper}
                    onLayout={onLayout}
                    onContentSizeChange={onContentSizeChange}
                    showsHorizontalScrollIndicator={showScrollIndicator}
                >
                    <Animated.View pointerEvents="none" style={[styles.indicator, indicatorStyle]} />

                    {productChips.map(product => (
                        <Box key={product}>
                            <ChipText product={product} isSelected={toProduct === product} />
                        </Box>
                    ))}
                    <Box>
                        <DefaultProductGearButton />
                    </Box>
                </Animated.ScrollView>
            </Box>
        );
    }
);

const ChipText = ({ product, isSelected }: { product: SelectableProduct; isSelected: boolean }) => {
    const { t } = useTranslation('product_switch_menu');

    const Icon = productToIconMap[product];

    return (
        <Row
            alignItems="center"
            paddingHorizontal={'s12'}
            paddingVertical={'s8'}
            gap={'s8'}
            opacity={isSelected ? 1 : 0.6}
        >
            <Icon />
            <Text variant="bodySmall2" fontWeight={'500'} color={ds.colorToken.text.primary}>
                {t(product)}
            </Text>
        </Row>
    );
};

export const productToIconMap: Record<SelectableProduct, React.ComponentType> = {
    [Product.Pickem]: PickemProductIcon,
    [Product.Sweeps]: SweepsProductIcon,
    [Product.Arcade]: ArcadeProductIcon,
    [Product.Casino]: CasinoProductIcon,
    [Product.Prediction]: PredictionProductIcon,
    [Product.Rips]: RipsProductIcon,
};

const styles = StyleSheet.create({
    indicator: {
        borderRadius: 18,
        top: 4,
        left: 0,
        position: 'absolute',
        backgroundColor: ds.colorValue.background.transparent.strong,
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: ds.colorValue.border.strong,
        height: 36,
    },
    contentWrapper: {
        flexGrow: 1,
        justifyContent: 'center',
        paddingHorizontal: 16,
        paddingTop: 4,
    },
    contentContainer: {
        flexGrow: 1,
        justifyContent: 'center',
    },
    gearButton: {
        padding: 10,
        borderRadius: 18,
        alignSelf: 'center',
        borderWidth: 1,
        borderColor: ds.colorValue.border.strong,
        backgroundColor: ds.colorValue.background.transparent.subtle,
        marginRight: 16,
        marginLeft: 4,
    },
});
